/**
 * Screencast Encoder
 *
 * Pipes CDP screencast JPEG frames into ffmpeg in real-time, outputting
 * fragmented MP4 (fMP4) chunks suitable for streaming via Media Source Extensions.
 *
 * Architecture:
 *   CDP frames (JPEG) -> ffmpeg stdin (image2pipe)
 *   ffmpeg encodes H.264 (ultrafast, zerolatency)
 *   fMP4 chunks from ffmpeg stdout -> parsed into init segment + media segments
 *   -> delivered via onSegment callback
 *
 * Lifecycle (spawn, stderr capture, close/error/stdin-error, stop-with-timeout)
 * is delegated to FfmpegProcess from this package; this class owns the fMP4
 * atom parser and segment emission.
 *
 * Used by both apps/api (live browser-session streaming) and the MicroVM
 * guest — the logger is injected by the host environment via
 * `BrowserLogger`, same pattern as ScreencastFileEncoder.
 *
 * @module screencast-encoder
 * @see docs/browser-session.md
 */

import { FfmpegProcess } from './ffmpeg-process.js';
import { consoleLogger, type BrowserLogger } from './logger.js';

interface ScreencastEncoderOptions {
  /** Input framerate. Default: 5 */
  framerate?: number;
  /** Fragment duration in microseconds. Default: 1_000_000 (1 second) */
  fragDurationUs?: number;
  /** Callback for the init segment (moov atom). Called once. */
  onInitSegment: (data: Buffer) => void;
  /** Callback for each media segment (moof+mdat). Called repeatedly. */
  onMediaSegment: (data: Buffer) => void;
  /** Callback when the encoder encounters an error or exits. */
  onError?: (error: Error) => void;
  /** Logger instance; defaults to consoleLogger. */
  logger?: BrowserLogger;
}

/**
 * Real-time screencast encoder that converts a stream of JPEG frames
 * into fragmented MP4 via ffmpeg.
 *
 * Usage:
 *   const encoder = new ScreencastEncoder({ framerate: 5, onInitSegment, onMediaSegment });
 *   encoder.start();
 *   encoder.pushFrame(jpegBuffer); // called for each CDP screencast frame
 *   await encoder.stop();
 */
export class ScreencastEncoder {
  private proc: FfmpegProcess | null = null;
  private readonly logger: BrowserLogger;
  private _started = false;
  private _stopped = false;
  private readonly options: Required<
    Pick<ScreencastEncoderOptions, 'framerate' | 'fragDurationUs'>
  > &
    ScreencastEncoderOptions;

  /** Buffer to accumulate ffmpeg stdout and parse MP4 atoms */
  private outputBuffer = Buffer.alloc(0);
  /** Whether the init segment (moov) has been emitted */
  private initSegmentEmitted = false;
  private frameCount = 0;
  /** Accumulator for init segment atoms (ftyp before moov) */
  private _initAccumulator: Buffer[] = [];
  /** Buffer for moof atom waiting for its mdat pair */
  private _moofBuffer: Buffer | null = null;

  constructor(options: ScreencastEncoderOptions) {
    this.options = {
      framerate: options.framerate ?? 5,
      fragDurationUs: options.fragDurationUs ?? 1_000_000,
      ...options,
    };
    this.logger = options.logger ?? consoleLogger;
  }

  get started(): boolean {
    return this._started;
  }

  get stopped(): boolean {
    return this._stopped;
  }

  /**
   * Start the ffmpeg process. Call this before pushing frames.
   */
  start(): void {
    if (this._started) return;
    this._started = true;

    const { framerate, fragDurationUs } = this.options;

    const args = [
      // Input: JPEG frames piped via stdin
      '-f',
      'image2pipe',
      '-framerate',
      String(framerate),
      '-i',
      'pipe:0',
      // H.264 encoding: fast, low-latency
      '-c:v',
      'libx264',
      '-preset',
      'ultrafast',
      '-tune',
      'zerolatency',
      // Baseline profile for maximum browser compatibility
      '-profile:v',
      'baseline',
      '-level',
      '3.0',
      // Keyframe every 2 seconds at the given framerate, no B-frames
      '-g',
      String(framerate * 2),
      '-bf',
      '0',
      // Pixel format for compatibility
      '-pix_fmt',
      'yuv420p',
      // Output: fragmented MP4 to stdout
      '-f',
      'mp4',
      '-movflags',
      'frag_keyframe+empty_moov+default_base_moof',
      '-frag_duration',
      String(fragDurationUs),
      'pipe:1',
    ];

    this.logger.info('[ScreencastEncoder] Starting ffmpeg', { framerate, fragDurationUs });

    this.proc = new FfmpegProcess({
      args,
      logTag: '[ScreencastEncoder]',
      logger: this.logger,
      stopTimeoutMs: 5_000,
      onStdout: (chunk) => {
        this.outputBuffer = Buffer.concat([this.outputBuffer, chunk]);
        this.parseAtoms();
      },
      onUnexpectedExit: (err) => {
        this.options.onError?.(err);
      },
    });
    this.proc.start();
  }

  /**
   * Push a JPEG frame into the encoder.
   * @param jpegBuffer - Raw JPEG bytes (not base64)
   */
  pushFrame(jpegBuffer: Buffer): void {
    if (!this._started || this._stopped || !this.proc?.stdinWritable) {
      return;
    }
    // Only count frames that the stream actually accepted; writeFrame returns
    // false on a stream that's been closed underneath us, in which case
    // bumping the count would lie about what reached ffmpeg.
    if (this.proc.writeFrame(jpegBuffer)) {
      this.frameCount++;
    }
  }

  /**
   * Stop the encoder gracefully. Closes stdin to signal EOF,
   * then waits for ffmpeg to flush remaining data and exit.
   */
  async stop(): Promise<void> {
    if (this._stopped) return;
    this._stopped = true;

    const proc = this.proc;
    this.proc = null;
    if (!proc) return;
    await proc.stop();
  }

  /**
   * Kill the encoder immediately without waiting for flush.
   */
  kill(): void {
    this._stopped = true;
    if (this.proc) {
      this.proc.kill();
      this.proc = null;
    }
  }

  /**
   * Parse MP4 box/atom boundaries from the output buffer.
   *
   * MP4 atoms have the structure: [4 bytes size][4 bytes type][payload]
   * We split on atom boundaries to separate the init segment (ftyp+moov)
   * from media segments (moof+mdat pairs).
   */
  private parseAtoms(): void {
    while (this.outputBuffer.length >= 8) {
      const size = this.outputBuffer.readUInt32BE(0);

      // Sanity check: size must be at least 8 and not absurdly large
      if (size < 8 || size > 50 * 1024 * 1024) {
        this.logger.warn('[ScreencastEncoder] Invalid atom size, resetting buffer', {
          size,
          bufferLength: this.outputBuffer.length,
        });
        this.outputBuffer = Buffer.alloc(0);
        return;
      }

      // Wait for the full atom
      if (this.outputBuffer.length < size) {
        return;
      }

      const type = this.outputBuffer.toString('ascii', 4, 8);
      const atom = this.outputBuffer.subarray(0, size);
      this.outputBuffer = this.outputBuffer.subarray(size);

      if (!this.initSegmentEmitted) {
        // Accumulate ftyp + moov as the init segment
        if (type === 'ftyp' || type === 'moov') {
          // If we get moov, we have the complete init segment
          if (type === 'moov') {
            // Look back for ftyp that we may have already buffered
            // In practice, ffmpeg with empty_moov outputs ftyp then moov immediately
            this.initSegmentEmitted = true;

            // We need to combine any previously seen ftyp with this moov
            if (this._initAccumulator.length > 0) {
              const initSegment = Buffer.concat([...this._initAccumulator, atom]);
              this.options.onInitSegment(initSegment);
              this._initAccumulator = [];
            } else {
              this.options.onInitSegment(Buffer.from(atom));
            }

            this.logger.debug('[ScreencastEncoder] Init segment emitted', {
              size: atom.length,
            });
          } else {
            // Accumulate (ftyp comes before moov)
            this._initAccumulator.push(Buffer.from(atom));
          }
        }
      } else {
        // After init segment, everything is media data (moof+mdat or other atoms)
        // Group moof+mdat pairs as single media segments
        if (type === 'moof') {
          this._moofBuffer = Buffer.from(atom);
        } else if (type === 'mdat' && this._moofBuffer) {
          const segment = Buffer.concat([this._moofBuffer, atom]);
          this._moofBuffer = null;
          this.options.onMediaSegment(segment);
        } else {
          // Standalone atom (e.g., sidx) — emit as media segment
          this.options.onMediaSegment(Buffer.from(atom));
        }
      }
    }
  }
}
